import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MDSpinner from "react-md-spinner";
import {
  FaChevronDown,
  FaChevronRight,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaGasPump,
  FaLeaf,
  FaMapMarkedAlt,
  FaPencilAlt,
  FaTachometerAlt,
  FaWrench,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { usePendingActions } from "../../contexts/PendingActionsContext";
import {
  acknowledgePendingAction,
  completeScheduledMaintenance,
  correctPendingMeterReading,
  updateSessionFinishReadings,
} from "./fieldAppApi";
import FieldAppCompleteMaintenanceModal from "./FieldAppCompleteMaintenanceModal";
import {
  METER_BOTH,
  METER_HOUR,
  METER_ODOMETER,
  normalizeMeterType,
  resolvePanelReadingValue,
} from "./fieldAppMeterLabels";
import {
  filterPendingByCategory,
  PENDING_ACTION_CATEGORIES,
  resolvePendingCategory,
} from "./pendingActionCategories";
import { buildFuelSupplySheet } from "./fuelSupplyAlertSheet";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatLocation(location) {
  if (!location || location.latitude == null || location.longitude == null) {
    return "—";
  }
  return `${Number(location.latitude).toFixed(6)}, ${Number(location.longitude).toFixed(6)}`;
}

function farmLabel(item) {
  return item.farmName || (item.farmId ? String(item.farmId) : "");
}

function anomalyKindLabel(kind, t) {
  const labels = {
    regression: t("Leitura abaixo da anterior"),
    excessive_jump: t("Salto grande na leitura"),
    excessive_rate: t("Horímetro acima do tempo de trabalho"),
  };
  return labels[kind] || kind || t("Inconsistência");
}

function meterAnomalyMeterLabel(meter, t) {
  if (meter === "odometer") {
    return t("Hodômetro");
  }
  if (meter === "hour_meter") {
    return t("Horímetro");
  }
  return t("Medidor");
}

function meterAnomalyUnit(meter) {
  return meter === "odometer" ? "km" : "h";
}

function formatMeterDelta(value, unit) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  const num = Number(value);
  const formatted = formatNumber(Math.abs(num));
  if (num > 0) {
    return `+${formatted} ${unit}`;
  }
  if (num < 0) {
    return `−${formatted} ${unit}`;
  }
  return `0 ${unit}`;
}

function meterTypesForItem(item) {
  const meterType = normalizeMeterType(item.meterType);
  if (meterType === METER_BOTH) {
    return [METER_HOUR, METER_ODOMETER];
  }
  if (meterType === METER_ODOMETER) {
    return [METER_ODOMETER];
  }
  return [METER_HOUR];
}

function coalesce(...values) {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value != null) {
      return value;
    }
  }
  return null;
}

function resolveAssumeReading(item, session, meter, anomalies) {
  const fromSession = sessionAssumeReading(session, meter);
  if (fromSession != null) {
    return fromSession;
  }
  const anomaly = (anomalies || []).find((row) => row && row.meter === meter);
  if (anomaly && anomaly.context !== "finish_below_session_start") {
    if (item.eventType === "ASSUMIR_MAQUINA") {
      return coalesce(anomaly.current, anomaly.reference, null);
    }
    return coalesce(anomaly.reference, null);
  }
  if (item.eventType === "ASSUMIR_MAQUINA") {
    return meter === METER_ODOMETER ? item.odometer : item.hourMeter;
  }
  return null;
}

function resolveFinishReading(item, session, meter, anomalies) {
  const fromSession = sessionFinishReading(session, meter);
  if (fromSession != null) {
    return fromSession;
  }
  const anomaly = (anomalies || []).find((row) => row && row.meter === meter);
  if (anomaly) {
    if (anomaly.context === "finish_below_session_start") {
      return coalesce(anomaly.current, null);
    }
    if (item.eventType === "FINALIZAR_TRABALHO") {
      return coalesce(anomaly.current, null);
    }
    return coalesce(anomaly.current, null);
  }
  if (item.eventType === "FINALIZAR_TRABALHO") {
    return meter === METER_ODOMETER ? item.odometer : item.hourMeter;
  }
  return null;
}

function sessionAssumeReading(session, meter) {
  if (!session) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? session.assumeOdometer
    : session.assumeHourMeter;
}

function sessionFinishReading(session, meter) {
  if (!session) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? session.finishOdometer
    : session.finishHourMeter;
}

function previousReadingValue(anomalies) {
  const match = (anomalies || []).find(
    (row) => row && row.reference != null && row.reference !== ""
  );
  return match ? match.reference : null;
}

function latestSystemReadingValue(latest, meter) {
  if (!latest) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? latest.odometer
    : latest.hourMeter;
}

function buildMeterAnomalyTableRows(item) {
  const session = item.sessionReadings || {};
  const latest = item.latestSystemReading || {};
  const anomaliesByMeter = (item.anomalies || []).reduce((acc, anomaly) => {
    if (!anomaly || !anomaly.meter) {
      return acc;
    }
    if (!acc[anomaly.meter]) {
      acc[anomaly.meter] = [];
    }
    acc[anomaly.meter].push(anomaly);
    return acc;
  }, {});

  return meterTypesForItem(item)
    .map((meter) => {
      const meterAnomalies = anomaliesByMeter[meter] || [];
      const assume = resolveAssumeReading(
        item,
        session,
        meter,
        meterAnomalies.length ? meterAnomalies : item.anomalies
      );
      const finish = resolveFinishReading(
        item,
        session,
        meter,
        meterAnomalies.length ? meterAnomalies : item.anomalies
      );
      const previousReading = previousReadingValue(
        meterAnomalies.length ? meterAnomalies : item.anomalies
      );
      const systemLatest = latestSystemReadingValue(latest, meter);
      const sessionDelta =
        finish != null && assume != null ? finish - assume : null;

      return {
        key: meter,
        meter,
        assume,
        finish,
        previousReading,
        systemLatest,
        sessionDelta,
        anomalies: meterAnomalies.length
          ? meterAnomalies
          : (item.anomalies || []).filter(
              (row) => !row || !row.meter || row.meter === meter
            ),
      };
    })
    .filter(
      (row) =>
        row.anomalies.length > 0 ||
        row.assume != null ||
        row.finish != null ||
        row.previousReading != null ||
        row.systemLatest != null
    );
}

function parseMeterDraft(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  const value = Number.parseFloat(text.replace(",", "."));
  if (Number.isNaN(value) || value < 0) return null;
  return value;
}

function CorrectMeterReadingButton({ item, t, onResolved, className }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hourDraft, setHourDraft] = useState("");
  const [odometerDraft, setOdometerDraft] = useState("");
  const [assumeHourDraft, setAssumeHourDraft] = useState("");
  const [assumeOdometerDraft, setAssumeOdometerDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");

  const meterType = normalizeMeterType(item.meterType) || METER_HOUR;
  const showHour =
    meterType === METER_HOUR || meterType === METER_BOTH || meterType == null;
  const showOdo =
    meterType === METER_ODOMETER || meterType === METER_BOTH;
  const isStale = item.actionType === "stale_meter_reading";
  const session = item.sessionReadings || {};

  const openEditor = () => {
    const finishHour =
      item.finishHourMeter != null
        ? item.finishHourMeter
        : session.finishHourMeter != null
          ? session.finishHourMeter
          : item.hourMeter;
    const finishOdo =
      item.finishOdometer != null
        ? item.finishOdometer
        : session.finishOdometer != null
          ? session.finishOdometer
          : item.odometer;
    const assumeHour =
      item.assumeHourMeter != null
        ? item.assumeHourMeter
        : session.assumeHourMeter;
    const assumeOdo =
      item.assumeOdometer != null
        ? item.assumeOdometer
        : session.assumeOdometer;

    setHourDraft(finishHour != null ? String(finishHour) : "");
    setOdometerDraft(finishOdo != null ? String(finishOdo) : "");
    setAssumeHourDraft(assumeHour != null ? String(assumeHour) : "");
    setAssumeOdometerDraft(assumeOdo != null ? String(assumeOdo) : "");
    setNoteDraft("");
    setError(null);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!item.recordUuid) {
      setError(t("Registro não identificado para correção."));
      return;
    }

    const hourMeter = showHour ? parseMeterDraft(hourDraft) : null;
    const odometer = showOdo ? parseMeterDraft(odometerDraft) : null;
    const assumeHourMeter =
      isStale && showHour ? parseMeterDraft(assumeHourDraft) : null;
    const assumeOdometer =
      isStale && showOdo ? parseMeterDraft(assumeOdometerDraft) : null;

    if (
      hourMeter == null &&
      odometer == null &&
      assumeHourMeter == null &&
      assumeOdometer == null
    ) {
      setError(t("Informe horímetro e/ou hodômetro corrigidos."));
      return;
    }

    // Só valida os campos visíveis/enviados — em inconsistência não há
    // campos de assumir, então drafts internos não podem barrar o salvamento.
    const invalidVisible =
      (showHour && hourDraft.trim() && hourMeter == null) ||
      (showOdo && odometerDraft.trim() && odometer == null) ||
      (isStale && showHour && assumeHourDraft.trim() && assumeHourMeter == null) ||
      (isStale && showOdo && assumeOdometerDraft.trim() && assumeOdometer == null);
    if (invalidVisible) {
      setError(t("Informe um valor numérico válido."));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await correctPendingMeterReading({
        recordUuid: item.recordUuid,
        actionType: item.actionType,
        hourMeter,
        odometer,
        assumeHourMeter,
        assumeOdometer,
        managerNote: noteDraft.trim() || undefined,
      });
      if (result && result.alertCleared) {
        toast.success(t("Leitura corrigida. O alerta saiu da lista."));
      } else {
        toast.success(
          t(
            "Leitura corrigida. Revise o alerta — ainda pode haver inconsistência."
          )
        );
      }
      setOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(t("Não foi possível corrigir a leitura. Tente novamente."));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className={className || "field-app-pending-action-actions"}>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={openEditor}
        >
          <FaPencilAlt style={{ marginRight: 6 }} />
          {t("Corrigir leitura")}
        </button>
      </div>
    );
  }

  return (
    <div className="field-app-pending-action-form">
      <p className="field-app-pending-action-hint">
        {t(
          "Informe o valor correto do painel. A correção atualiza o registro do operador e reavalia o alerta."
        )}
      </p>
      {isStale ? (
        <>
          {showHour ? (
            <label>
              {t("Horímetro ao assumir (h)")}
              <input
                type="number"
                step="0.1"
                min="0"
                value={assumeHourDraft}
                disabled={saving}
                onChange={(event) => setAssumeHourDraft(event.target.value)}
              />
            </label>
          ) : null}
          {showOdo ? (
            <label>
              {t("Hodômetro ao assumir (km)")}
              <input
                type="number"
                step="0.1"
                min="0"
                value={assumeOdometerDraft}
                disabled={saving}
                onChange={(event) => setAssumeOdometerDraft(event.target.value)}
              />
            </label>
          ) : null}
        </>
      ) : null}
      {showHour ? (
        <label>
          {isStale
            ? t("Horímetro ao sair (h)")
            : t("Horímetro corrigido (h)")}
          <input
            type="number"
            step="0.1"
            min="0"
            value={hourDraft}
            disabled={saving}
            onChange={(event) => setHourDraft(event.target.value)}
          />
        </label>
      ) : null}
      {showOdo ? (
        <label>
          {isStale
            ? t("Hodômetro ao sair (km)")
            : t("Hodômetro corrigido (km)")}
          <input
            type="number"
            step="0.1"
            min="0"
            value={odometerDraft}
            disabled={saving}
            onChange={(event) => setOdometerDraft(event.target.value)}
          />
        </label>
      ) : null}
      <label>
        {t("Observação (opcional)")}
        <textarea
          rows={2}
          value={noteDraft}
          disabled={saving}
          onChange={(event) => setNoteDraft(event.target.value)}
          placeholder={t("Ex.: valor conferido na foto do painel.")}
        />
      </label>
      {error ? (
        <p className="field-app-pending-action-error">{error}</p>
      ) : null}
      <div className="field-app-pending-action-actions">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? t("Salvando…") : t("Salvar correção")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default"
          disabled={saving}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          {t("Cancelar")}
        </button>
      </div>
    </div>
  );
}

function AcknowledgeAlertButton({ item, t, onResolved, className }) {
  const [saving, setSaving] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const handleAcknowledge = async () => {
    if (!item.recordUuid) {
      return;
    }
    setSaving(true);
    try {
      await acknowledgePendingAction({
        recordUuid: item.recordUuid,
        actionType: item.actionType,
        managerNote: noteDraft.trim() || undefined,
      });
      toast.success(t("Alerta reconhecido e removido da lista."));
      setNoteOpen(false);
      setNoteDraft("");
      if (onResolved) {
        await onResolved();
      }
    } catch (error) {
      toast.error(
        t("Não foi possível reconhecer o alerta. Tente novamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className || "field-app-pending-action-actions"}>
      {noteOpen ? (
        <div className="field-app-pending-action-form">
          <label htmlFor={`ack-note-${item.recordUuid}`}>
            {t("Observação (opcional)")}
          </label>
          <textarea
            id={`ack-note-${item.recordUuid}`}
            rows={2}
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder={t("Ex.: conferido com operador; leitura corrigida no painel.")}
          />
        </div>
      ) : null}
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={saving}
        onClick={() => {
          if (noteOpen) {
            handleAcknowledge();
            return;
          }
          setNoteOpen(true);
        }}
      >
        {noteOpen ? t("Confirmar reconhecimento") : t("Reconhecer / dispensar")}
      </button>
      {noteOpen ? (
        <button
          type="button"
          className="btn btn-sm btn-default"
          disabled={saving}
          onClick={() => {
            setNoteOpen(false);
            setNoteDraft("");
          }}
        >
          {t("Cancelar")}
        </button>
      ) : null}
    </div>
  );
}

function readingEventLabel(label, t) {
  const known = {
    CHECK_CALIBRAGEM: "Check de calibragem",
    PAUSA_INTERVALO: "Pausa",
    PERDA_COLHEITA_AMOSTRA: "Perda na colheita",
  };
  return t(known[label] || label);
}

function readingPopupPosition(event, { editing } = {}) {
  const rect = event.currentTarget.getBoundingClientRect();
  const popupHeight = editing ? 320 : 220;
  const popupWidth = 300;
  const below = rect.bottom + 8;
  const top =
    below + popupHeight > window.innerHeight
      ? Math.max(8, rect.top - popupHeight - 8)
      : below;
  return {
    top,
    left: Math.max(8, Math.min(rect.left, window.innerWidth - popupWidth)),
  };
}

function ReadingNumber({
  value,
  unit,
  label,
  source,
  emphasized,
  onOpen,
  meter,
  role,
  correctable,
}) {
  if (value == null || value === "") {
    return "—";
  }
  const valueText = `${formatNumber(value)}${unit ? ` ${unit}` : ""}`;
  const canCorrect = Boolean(
    correctable && source && source.recordUuid
  );
  return (
    <button
      type="button"
      className={
        canCorrect
          ? "field-app-reading-link field-app-reading-link--correctable"
          : "field-app-reading-link"
      }
      title={
        canCorrect ? `${label} — clique para corrigir` : label
      }
      onClick={(event) =>
        onOpen({
          label,
          valueText,
          value,
          unit,
          meter,
          role,
          correctable: canCorrect,
          source: source || {},
          ...readingPopupPosition(event),
        })
      }
    >
      <span
        className={
          emphasized
            ? "field-app-pending-action-meter-value field-app-pending-action-meter-value--current"
            : "field-app-pending-action-meter-value"
        }
      >
        {valueText}
      </span>
    </button>
  );
}

function ReadingSourcePopup({
  detail,
  t,
  item,
  onClose,
  onOpenTracking,
  onResolved,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!detail) {
      return;
    }
    setEditing(false);
    setDraft(detail.value != null ? String(detail.value) : "");
    setNoteDraft("");
    setError(null);
  }, [detail]);

  if (!detail) return null;
  const source = detail.source || {};
  const isOdometer =
    detail.meter === METER_ODOMETER || detail.meter === "odometer";
  const unitLabel = detail.unit || (isOdometer ? "km" : "h");

  const handleSaveCorrection = async () => {
    if (!item || !item.recordUuid || !source.recordUuid) {
      setError(t("Registro não identificado para correção."));
      return;
    }
    const parsed = parseMeterDraft(draft);
    if (parsed == null) {
      setError(t("Informe um valor numérico válido."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await correctPendingMeterReading({
        recordUuid: item.recordUuid,
        correctRecordUuid: source.recordUuid,
        targetRole: detail.role,
        actionType: item.actionType,
        hourMeter: isOdometer ? null : parsed,
        odometer: isOdometer ? parsed : null,
        managerNote: noteDraft.trim() || undefined,
      });
      if (result && result.alertCleared) {
        toast.success(t("Leitura corrigida. O alerta saiu da lista."));
      } else {
        toast.success(
          t(
            "Leitura corrigida. Revise o alerta — ainda pode haver inconsistência."
          )
        );
      }
      onClose();
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(t("Não foi possível corrigir a leitura. Tente novamente."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={
        editing
          ? "field-app-reading-popup field-app-reading-popup--editing"
          : "field-app-reading-popup"
      }
      style={{ top: detail.top, left: detail.left }}
      role="dialog"
      aria-label={detail.label}
    >
      <div className="field-app-reading-popup__head">
        <strong>{detail.label}</strong>
        <button
          type="button"
          className="field-app-reading-popup__map"
          onClick={() => onOpenTracking(source)}
          aria-label={t("Abrir rastreamento desta máquina")}
          title={t("Abrir rastreamento desta máquina")}
        >
          <FaMapMarkedAlt aria-hidden />
        </button>
        <button
          type="button"
          className="field-app-reading-popup__close"
          onClick={onClose}
          aria-label={t("Fechar")}
        >
          ×
        </button>
      </div>
      {!editing ? (
        <p className="field-app-reading-popup__value">{detail.valueText}</p>
      ) : null}
      <p>
        <strong>{t("Quem lançou")}:</strong> {source.operatorName || "—"}
      </p>
      <p>
        <strong>{t("Data")}:</strong> {formatDateTime(source.at)}
      </p>
      {source.eventLabel ? <p>{readingEventLabel(source.eventLabel, t)}</p> : null}

      {detail.correctable && !editing ? (
        <div className="field-app-reading-popup__actions">
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => {
              setDraft(detail.value != null ? String(detail.value) : "");
              setNoteDraft("");
              setError(null);
              setEditing(true);
            }}
          >
            <FaPencilAlt style={{ marginRight: 6 }} />
            {t("Corrigir esta leitura")}
          </button>
        </div>
      ) : null}

      {editing ? (
        <div className="field-app-reading-popup__form">
          <p className="field-app-pending-action-hint">
            {t(
              "Informe o valor correto só desta coluna. Os outros lançamentos não mudam."
            )}
          </p>
          <label>
            {isOdometer
              ? t("Hodômetro corrigido (km)")
              : t("Horímetro corrigido (h)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={draft}
              disabled={saving}
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Observação (opcional)")}
            <textarea
              rows={2}
              value={noteDraft}
              disabled={saving}
              onChange={(event) => setNoteDraft(event.target.value)}
              placeholder={t("Ex.: valor conferido na foto do painel.")}
            />
          </label>
          {error ? (
            <p className="field-app-pending-action-error">{error}</p>
          ) : null}
          <div className="field-app-reading-popup__actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={saving}
              onClick={handleSaveCorrection}
            >
              {saving ? t("Salvando…") : t("Salvar correção")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-default"
              disabled={saving}
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              {t("Cancelar")}
            </button>
          </div>
          <p className="field-app-reading-popup__unit-hint">
            {t("Unidade")}: {unitLabel}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function useReadingPopup() {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!detail) return undefined;
    const close = (event) => {
      if (event.target.closest && event.target.closest(".field-app-reading-popup, .field-app-reading-link")) {
        return;
      }
      setDetail(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [detail]);

  return [detail, setDetail];
}

function localDayKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function openReadingOnMap(item, source, onOpenInTracking) {
  if (!onOpenInTracking) return;
  onOpenInTracking({
    ...item,
    finishedAt: localDayKey(source && source.at) || item.finishedAt,
    operatorId:
      source && source.operatorId != null ? source.operatorId : item.operatorId,
    operatorName:
      (source && source.operatorName) || item.operatorName,
    sessionUuid: (source && source.sessionUuid) || item.sessionUuid || null,
  });
}

function meterReadingSource(item, key) {
  const sources = item.readingSources || {};
  if (sources[key]) return sources[key];
  if (key === "finish" && item.eventType === "FINALIZAR_TRABALHO") {
    return {
      recordUuid: item.recordUuid || null,
      operatorId: item.operatorId,
      operatorName: item.operatorName || "",
      at: item.finishedAt,
      eventLabel: item.eventContextLabel || "",
      sessionUuid: item.sessionUuid || "",
    };
  }
  if (key === "assume" && item.eventType === "ASSUMIR_MAQUINA") {
    return {
      recordUuid: item.recordUuid || null,
      operatorId: item.operatorId,
      operatorName: item.operatorName || "",
      at: item.finishedAt,
      eventLabel: item.eventContextLabel || "",
      sessionUuid: item.sessionUuid || "",
    };
  }
  return {};
}

function MeterAnomalyActionCard({ item, t, onOpenInTracking, onResolved }) {
  const tableRows = buildMeterAnomalyTableRows(item);
  const [readingPopup, setReadingPopup] = useReadingPopup();

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--meter-anomaly">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--meter-anomaly">
            <FaExclamationTriangle aria-hidden />
            {t(item.actionLabel || "Leitura inconsistente de odômetro ou horímetro")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Contexto")}: {item.eventContextLabel || item.eventType || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--meter-anomaly">
          {t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {t(
          "Compare leituras ao assumir e ao sair da máquina com a última leitura registrada no sistema e confira o motivo de cada alerta."
        )}
      </p>
      <p className="field-app-pending-action-hint">
        {t(
          "Clique no número que estiver errado (anterior, ao assumir ou ao sair) para corrigir só aquele valor."
        )}
      </p>

      {tableRows.length > 0 ? (
        <div className="field-app-pending-action-meter-table-wrap">
          <table className="field-app-pending-action-meter-table">
            <thead>
              <tr>
                <th>{t("Medidor")}</th>
                <th>{t("Leitura anterior")}</th>
                <th>{t("Leitura ao assumir")}</th>
                <th>{t("Leitura ao sair")}</th>
                <th>{t("Última leitura no sistema")}</th>
                <th>{t("Percorrido na sessão")}</th>
                <th>{t("Motivo")}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                const unit = meterAnomalyUnit(row.meter);
                const deltaClass =
                  row.sessionDelta != null && Number(row.sessionDelta) < 0
                    ? "field-app-pending-action-meter-delta--negative"
                    : row.sessionDelta != null && Number(row.sessionDelta) > 0
                      ? "field-app-pending-action-meter-delta--positive"
                      : "";

                return (
                  <tr key={row.key}>
                    <td>{meterAnomalyMeterLabel(row.meter, t)}</td>
                    <td>
                      <ReadingNumber
                        value={row.previousReading}
                        unit={unit}
                        label={t("Leitura anterior")}
                        source={meterReadingSource(item, "previous")}
                        meter={row.meter}
                        role="previous"
                        correctable
                        onOpen={setReadingPopup}
                      />
                    </td>
                    <td>
                      <ReadingNumber
                        value={row.assume}
                        unit={unit}
                        label={t("Leitura ao assumir")}
                        source={meterReadingSource(item, "assume")}
                        meter={row.meter}
                        role="assume"
                        correctable
                        onOpen={setReadingPopup}
                      />
                    </td>
                    <td>
                      <ReadingNumber
                        value={row.finish}
                        unit={unit}
                        label={t("Leitura ao sair")}
                        source={meterReadingSource(item, "finish")}
                        meter={row.meter}
                        role="finish"
                        correctable
                        emphasized
                        onOpen={setReadingPopup}
                      />
                    </td>
                    <td>
                      <ReadingNumber
                        value={row.systemLatest}
                        unit={unit}
                        label={t("Última leitura no sistema")}
                        source={meterReadingSource(item, "systemLatest")}
                        meter={row.meter}
                        role="systemLatest"
                        correctable
                        onOpen={setReadingPopup}
                      />
                    </td>
                    <td className={deltaClass}>
                      {formatMeterDelta(row.sessionDelta, unit)}
                    </td>
                    <td>
                      {row.anomalies.length > 0 ? (
                        row.anomalies.map((anomaly, index) => (
                          <div key={`${anomaly.kind}-${index}`}>
                            <strong>{anomalyKindLabel(anomaly.kind, t)}</strong>
                            {anomaly.message ? (
                              <span className="field-app-pending-action-meter-detail">
                                {anomaly.message}
                              </span>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="field-app-pending-action-readings">
          {item.hourMeter != null ? (
            <div>
              <strong>{t("Horímetro informado")}</strong>
              <ReadingNumber
                value={item.hourMeter}
                unit="h"
                label={t("Horímetro informado")}
                source={meterReadingSource(
                  item,
                  item.eventType === "ASSUMIR_MAQUINA" ? "assume" : "finish"
                )}
                meter={METER_HOUR}
                role={
                  item.eventType === "ASSUMIR_MAQUINA" ? "assume" : "finish"
                }
                correctable
                onOpen={setReadingPopup}
              />
            </div>
          ) : null}
          {item.odometer != null ? (
            <div>
              <strong>{t("Hodômetro informado")}</strong>
              <ReadingNumber
                value={item.odometer}
                unit="km"
                label={t("Hodômetro informado")}
                source={meterReadingSource(
                  item,
                  item.eventType === "ASSUMIR_MAQUINA" ? "assume" : "finish"
                )}
                meter={METER_ODOMETER}
                role={
                  item.eventType === "ASSUMIR_MAQUINA" ? "assume" : "finish"
                }
                correctable
                onOpen={setReadingPopup}
              />
            </div>
          ) : null}
        </div>
      )}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking && item.sessionUuid ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <ReadingSourcePopup
        detail={readingPopup}
        t={t}
        item={item}
        onClose={() => setReadingPopup(null)}
        onOpenTracking={(source) => {
          setReadingPopup(null);
          openReadingOnMap(item, source, onOpenInTracking);
        }}
        onResolved={onResolved}
      />

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function fuelAnomalyKindLabel(kind, t) {
  const labels = {
    tank_over_capacity: t("Litros acima da capacidade do tanque"),
    tank_full_mismatch: t(
      "Tanque cheio inconsistente com abastecimentos do período"
    ),
    fuel_without_meter_progress: t(
      "Abastecimento sem avanço no medidor (consumo sem horas/km registradas)"
    ),
    consumption_above_max: t("Consumo acima do máximo cadastrado"),
    consumption_below_min: t("Consumo abaixo do mínimo cadastrado"),
    consumption_above_max_kpl: t("Rendimento acima do máximo cadastrado (km/L)"),
    consumption_below_min_kpl: t("Rendimento abaixo do mínimo cadastrado (km/L)"),
    regression: t("Horímetro abaixo da leitura anterior"),
    excessive_jump: t("Salto grande no horímetro"),
    excessive_rate: t("Horímetro acima do tempo de trabalho"),
  };
  return labels[kind] || anomalyKindLabel(kind, t);
}

function fuelSheetStatusLabel(status, t) {
  if (status === "bad") return t("Errado");
  if (status === "ok") return t("Certo");
  if (status === "info") return t("Na conta");
  return "—";
}

function fuelSheetReasonLabel(reason, t) {
  const labels = {
    used_in_sum: t("Entrou na soma do tanque"),
    meter_reference: t("Leitura de referência do medidor"),
    over_capacity: t("Litros acima da capacidade do tanque"),
    tank_full_sum: t("Tanque marcado cheio, mas a soma passa da capacidade"),
    meter_stuck: t("Medidor não avançou desde a última leitura"),
  };
  return labels[reason] || fuelAnomalyKindLabel(reason, t);
}

function fuelSheetSummaryLines(sheet, t) {
  const lines = [];
  if (sheet.mismatch) {
    const mismatch = sheet.mismatch;
    lines.push(
      t(
        "{{previous}} L do abastecimento anterior + {{current}} L deste abastecimento − {{used}} L de consumo mínimo = {{total}} L, acima do tanque de {{tank}} L.",
        {
          previous: formatNumber(mismatch.previousLiters),
          current: formatNumber(mismatch.currentLiters),
          used: formatNumber(mismatch.estimatedConsumption),
          total: formatNumber(mismatch.impliedLiters),
          tank: formatNumber(mismatch.tankCapacity || sheet.tankCapacity),
        }
      )
    );
  } else if (sheet.over) {
    lines.push(
      t("{{liters}} L abastecidos, acima do tanque de {{tank}} L.", {
        liters: formatNumber(sheet.over.liters),
        tank: formatNumber(sheet.over.tankCapacity || sheet.tankCapacity),
      })
    );
  }
  if (sheet.stuck) {
    lines.push(
      t("O medidor permaneceu em {{reading}} por {{hours}} h.", {
        reading: formatNumber(sheet.stuck.meterReading),
        hours: formatNumber(sheet.stuck.hoursElapsed),
      })
    );
  }
  (sheet.otherKinds || []).forEach((kind) => {
    lines.push(fuelAnomalyKindLabel(kind, t));
  });
  return lines;
}

function FuelSheetCell({ value, unit, status, label, source, onOpen, t }) {
  return (
    <td className={status === "bad" ? "field-app-fuel-sheet__bad" : undefined}>
      <ReadingNumber
        value={value}
        unit={unit}
        label={label}
        source={source}
        onOpen={onOpen}
      />
      {status && status !== "neutral" ? (
        <span className={`field-app-fuel-sheet__mark field-app-fuel-sheet__mark--${status}`}>
          {fuelSheetStatusLabel(status, t)}
        </span>
      ) : null}
    </td>
  );
}

function fuelReadingSource(row) {
  return {
    operatorId: row.operatorId,
    operatorName: row.operatorName || "",
    at: row.at,
    eventLabel:
      row.role === "previous" ? "Abastecimento anterior" : "Abastecimento",
    sessionUuid: "",
  };
}

function FuelSupplyAnomalyActionCard({ item, t, onResolved, onOpenInTracking }) {
  const sheet = buildFuelSupplySheet(item);
  const [readingPopup, setReadingPopup] = useReadingPopup();
  const meterIsOdometer = item.meterType === "odometer";
  const meterLabel = meterIsOdometer ? t("Hodômetro") : t("Horímetro");
  const meterUnit = meterIsOdometer ? "km" : "h";
  const summaryLines = fuelSheetSummaryLines(sheet, t);
  const registeredRange =
    item.consumptionMinKpl != null || item.consumptionMaxKpl != null
      ? `${formatNumber(item.consumptionMinKpl)} – ${formatNumber(item.consumptionMaxKpl)} km/L`
      : item.consumptionMinLph != null || item.consumptionMaxLph != null
        ? `${formatNumber(item.consumptionMinLph)} – ${formatNumber(item.consumptionMaxLph)} L/h`
        : "";

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--fuel">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--fuel">
            <FaGasPump aria-hidden />
            {t(item.actionLabel || "Abastecimento inconsistente")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--fuel">
          {t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {t(
          "Cada linha é um abastecimento. A situação de cada coluna diz o que está certo, o que está errado, e o resumo explica o porquê do alerta."
        )}
        {item.tankCapacity != null
          ? ` ${t("Capacidade do tanque")}: ${formatNumber(item.tankCapacity)} L.`
          : ""}
        {registeredRange
          ? ` ${t("Faixa cadastrada")}: ${registeredRange}.`
          : ""}
      </p>

      <div className="field-app-pending-action-meter-table-wrap">
        <table className="field-app-pending-action-meter-table field-app-fuel-sheet">
          <thead>
            <tr>
              <th>{t("Abastecimento")}</th>
              <th>{t("Data e hora")}</th>
              <th>{t("Litros")}</th>
              <th>{meterLabel}</th>
              <th>{t("Tanque cheio")}</th>
              <th>{t("Situação")}</th>
              <th>{t("Motivo")}</th>
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((row) => (
              <tr key={row.key}>
                <td>
                  {row.role === "previous"
                    ? t("Abastecimento anterior")
                    : t("Este abastecimento")}
                </td>
                <td>{formatDateTime(row.at)}</td>
                <FuelSheetCell
                  value={row.liters}
                  unit="L"
                  status={row.litersStatus}
                  label={t("Litros abastecidos")}
                  source={fuelReadingSource(row)}
                  onOpen={setReadingPopup}
                  t={t}
                />
                <FuelSheetCell
                  value={row.hourMeter}
                  unit={meterUnit}
                  status={row.meterStatus}
                  label={meterLabel}
                  source={fuelReadingSource(row)}
                  onOpen={setReadingPopup}
                  t={t}
                />
                <td
                  className={
                    row.tankStatus === "bad" ? "field-app-fuel-sheet__bad" : undefined
                  }
                >
                  <span className="field-app-pending-action-meter-value">
                    {row.tankFull == null ? "—" : row.tankFull ? t("Sim") : t("Não")}
                  </span>
                  {row.tankStatus !== "neutral" ? (
                    <span
                      className={`field-app-fuel-sheet__mark field-app-fuel-sheet__mark--${row.tankStatus}`}
                    >
                      {fuelSheetStatusLabel(row.tankStatus, t)}
                    </span>
                  ) : null}
                </td>
                <td
                  className={
                    row.overall === "bad" ? "field-app-fuel-sheet__bad" : undefined
                  }
                >
                  {fuelSheetStatusLabel(row.overall, t)}
                </td>
                <td>
                  {row.reasons.length
                    ? row.reasons.map((reason) => (
                        <span key={reason} className="field-app-fuel-sheet__reason">
                          {fuelSheetReasonLabel(reason, t)}
                        </span>
                      ))
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>{t("Resumo")}</th>
              <td colSpan={6}>
                <span className="field-app-fuel-sheet__mark field-app-fuel-sheet__mark--bad">
                  {t("Errado")}
                </span>
                {summaryLines.length
                  ? summaryLines.map((line) => (
                      <span key={line} className="field-app-fuel-sheet__reason">
                        {line}
                      </span>
                    ))
                  : null}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReadingSourcePopup
        detail={readingPopup}
        t={t}
        onClose={() => setReadingPopup(null)}
        onOpenTracking={(source) => {
          setReadingPopup(null);
          openReadingOnMap(item, source, onOpenInTracking);
        }}
      />

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function RemoteFinishActionCard({
  item,
  t,
  onResolved,
  onOpenInTracking,
  onEditingChange,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [hourDraft, setHourDraft] = useState("");
  const [odometerDraft, setOdometerDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [acceptNoteDraft, setAcceptNoteDraft] = useState("");
  const [acceptNoteOpen, setAcceptNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!onEditingChange) {
      return undefined;
    }
    const isEditing = editorOpen || acceptNoteOpen;
    if (!isEditing) {
      return undefined;
    }
    onEditingChange(true);
    return () => onEditingChange(false);
  }, [acceptNoteOpen, editorOpen, onEditingChange]);

  const acceptWithoutData = async () => {
    if (!item.recordUuid) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await acknowledgePendingAction({
        recordUuid: item.recordUuid,
        actionType: item.actionType,
        managerNote: acceptNoteDraft.trim() || undefined,
        acceptWithoutData: true,
      });
      toast.success(
        t("Encerramento aceito sem incluir horímetro ou hodômetro.")
      );
      setAcceptNoteOpen(false);
      setAcceptNoteDraft("");
      setEditorOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(
        t("Não foi possível aceitar o encerramento. Tente novamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  const saveClosure = async () => {
    const hourMeter =
      hourDraft.trim() === ""
        ? null
        : Number.parseFloat(String(hourDraft).replace(",", "."));
    const odometer =
      odometerDraft.trim() === ""
        ? null
        : Number.parseFloat(String(odometerDraft).replace(",", "."));

    if (hourMeter == null && odometer == null) {
      setError(
        t("Informe horímetro e/ou hodômetro final, ou abra no rastreamento.")
      );
      return;
    }

    if (!item.sessionUuid) {
      setError(t("Sessão não identificada para este registro."));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateSessionFinishReadings(item.farmId, {
        sessionUuid: item.sessionUuid,
        hourMeter,
        odometer,
        managerNote: noteDraft.trim() || null,
      });
      toast.success(t("Encerramento salvo."));
      setEditorOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(t("Não foi possível salvar o encerramento."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--remote">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type">
            <FaExclamationTriangle aria-hidden />
            {t(item.actionLabel || "Esqueci de finalizar a máquina")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge">{t("Pendente")}</span>
      </header>

      {item.observations ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Observação do operador")}:</strong> {item.observations}
        </p>
      ) : null}

      {item.finishObservationAudioUrl ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Áudio do operador")}:</strong>{" "}
          <audio controls preload="none" src={item.finishObservationAudioUrl}>
            {t("Seu navegador não reproduz áudio.")}
          </audio>
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        <strong>{t("Local informado")}:</strong> {formatLocation(item.location)}
      </p>

      <p className="field-app-pending-action-hint">
        {t(
          "O operador pode assumir esta máquina normalmente no dia seguinte. Esta pendência não bloqueia novo trabalho — complete o encerramento quando puder (mesmo dias depois)."
        )}
      </p>

      <div className="field-app-pending-action-actions">
        <button
          type="button"
          className="btn btn-sm btn-warning"
          onClick={() => setEditorOpen((current) => !current)}
          disabled={saving}
        >
          {editorOpen ? t("Fechar editor") : t("Completar encerramento")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default field-app-pending-action-accept-without-data"
          disabled={saving || acceptNoteOpen}
          onClick={() => setAcceptNoteOpen(true)}
        >
          <FaCheckCircle aria-hidden style={{ marginRight: 6 }} />
          {t("Aceitar encerramento sem incluir dados")}
        </button>
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
            disabled={saving}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Abrir no rastreamento")}
          </button>
        ) : null}
      </div>

      {acceptNoteOpen ? (
        <div className="field-app-pending-action-form field-app-pending-action-form--accept">
          <p className="field-app-pending-action-hint">
            {t(
              "O encerramento será removido da lista sem informar horímetro ou hodômetro final."
            )}
          </p>
          <label>
            {t("Anotação do gestor (opcional)")}
            <textarea
              rows={2}
              value={acceptNoteDraft}
              disabled={saving}
              onChange={(event) => setAcceptNoteDraft(event.target.value)}
              placeholder={t(
                "Ex.: operador confirmou que a máquina já estava encerrada no campo."
              )}
            />
          </label>
          <div className="field-app-pending-action-form-actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={saving}
              onClick={acceptWithoutData}
            >
              {saving ? t("Salvando…") : t("Confirmar aceite")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-default"
              disabled={saving}
              onClick={() => {
                setAcceptNoteOpen(false);
                setAcceptNoteDraft("");
              }}
            >
              {t("Cancelar")}
            </button>
          </div>
        </div>
      ) : null}

      {editorOpen ? (
        <div className="field-app-pending-action-form">
          <label>
            {t("Horímetro final (h)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={hourDraft}
              disabled={saving}
              onChange={(event) => setHourDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Hodômetro final (km)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={odometerDraft}
              disabled={saving}
              onChange={(event) => setOdometerDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Anotação do gestor (opcional)")}
            <textarea
              rows={2}
              value={noteDraft}
              disabled={saving}
              onChange={(event) => setNoteDraft(event.target.value)}
            />
          </label>
          {error ? (
            <p className="field-app-pending-action-error">{error}</p>
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving}
            onClick={saveClosure}
          >
            {saving ? t("Salvando…") : t("Salvar encerramento")}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function staleMeterDefectSuffix(staleMeters, t) {
  if (!staleMeters || staleMeters.length === 0) {
    return "";
  }
  return ` ${t("Pode indicar defeito no painel ou sensor.")}`;
}

function staleMeterReadingPair(item, meterKind) {
  const meterType =
    meterKind === "odometer" ? METER_ODOMETER : METER_HOUR;
  const startField =
    meterKind === "odometer" ? "assumeOdometer" : "assumeHourMeter";
  const finishField =
    meterKind === "odometer" ? "finishOdometer" : "finishHourMeter";
  const start =
    item[startField] != null
      ? item[startField]
      : resolvePanelReadingValue(
          {
            hourMeter: item.assumeHourMeter,
            odometer: item.assumeOdometer,
          },
          meterType
        );
  const finish =
    item[finishField] != null
      ? item[finishField]
      : resolvePanelReadingValue(
          {
            hourMeter: item.finishHourMeter,
            odometer: item.finishOdometer,
          },
          meterType
        );
  return { start, finish };
}

function StaleMeterActionCard({ item, t, onOpenInTracking, onResolved }) {
  const staleLabels = item.staleMeterLabels || [];
  const labelText =
    staleLabels.length > 0
      ? staleLabels.join(" e ")
      : t("horímetro/hodômetro");
  const defectSuffix = staleMeterDefectSuffix(item.staleMeters, t);
  const hourReadings = staleMeterReadingPair(item, "hour_meter");
  const odometerReadings = staleMeterReadingPair(item, "odometer");

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--meter">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--meter">
            <FaWrench aria-hidden />
            {t(item.actionLabel || "Odômetro ou horímetro sem variação no trabalho")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
            {item.machineStaleCount > 0 ? (
              <span>
                {t("Ocorrências na máquina")}: {item.machineStaleCount}
              </span>
            ) : null}
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--meter">
          {t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {item.pathDistanceM != null && item.pathDistanceM >= 100
          ? `${t(
              "O operador percorreu cerca de {{distance}} m no campo, mas informou a mesma leitura de {{meters}} ao assumir e ao finalizar",
              {
                distance: Math.round(item.pathDistanceM),
                meters: labelText,
              }
            )}${defectSuffix}`
          : `${t(
              "O operador informou a mesma leitura de {{meters}} ao assumir e ao finalizar o trabalho",
              { meters: labelText }
            )}${defectSuffix}`}
      </p>

      <div className="field-app-pending-action-readings">
        {(item.staleMeters || []).includes("hour_meter") ? (
          <div>
            <strong>{t("Horímetro")}</strong>
            <span>
              {t("Início")}: {formatNumber(hourReadings.start)} h → {t("Fim")}:{" "}
              {formatNumber(hourReadings.finish)} h
            </span>
          </div>
        ) : null}
        {(item.staleMeters || []).includes("odometer") ? (
          <div>
            <strong>{t("Hodômetro")}</strong>
            <span>
              {t("Início")}: {formatNumber(odometerReadings.start)} km → {t("Fim")}:{" "}
              {formatNumber(odometerReadings.finish)} km
            </span>
          </div>
        ) : null}
      </div>

      {item.machineMeterProblem ? (
        <p className="field-app-pending-action-problem">
          <FaWrench aria-hidden />
          {t(
            "Esta máquina foi marcada com possível problema no medidor. Horas, quilometragem e manutenções podem estar comprometidos — verifique o painel ou o sensor."
          )}
        </p>
      ) : (
        <p className="field-app-pending-action-hint">
          {t(
            "Máquinas paradas (<100 m) só geram alerta após 3 trabalhos seguidos com leitura igual. Com deslocamento no campo, o alerta é imediato."
          )}
        </p>
      )}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <CorrectMeterReadingButton item={item} t={t} onResolved={onResolved} />
      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function SpeedViolationActionCard({ item, t, onOpenInTracking, onResolved }) {
  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--speed">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--speed">
            <FaTachometerAlt aria-hidden />
            {t(item.actionLabel || "Velocidade acima do limite")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--speed">
          {item.inProgress ? t("Em andamento") : t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {item.inProgress
          ? t(
              "Trabalho ainda aberto. A máquina ultrapassou {{limit}} km/h (pico {{peak}} km/h) e já ficou {{duration}} acima do limite — cerca de {{percent}}% do tempo desde que assumiu a máquina ({{sessionDuration}}).",
              {
                limit: formatNumber(item.maxSpeedKmh),
                peak: formatNumber(item.peakSpeedKmh),
                duration:
                  item.secondsAboveLabel || formatNumber(item.secondsAboveLimit),
                percent: formatNumber(item.percentAboveLimit),
                sessionDuration:
                  item.sessionDurationLabel || formatNumber(item.sessionSeconds),
              }
            )
          : t(
              "A máquina ultrapassou a velocidade máxima configurada ({{limit}} km/h), atingindo {{peak}} km/h. Ficou {{duration}} acima do limite — cerca de {{percent}}% do tempo total do trabalho ({{sessionDuration}}).",
              {
                limit: formatNumber(item.maxSpeedKmh),
                peak: formatNumber(item.peakSpeedKmh),
                duration:
                  item.secondsAboveLabel || formatNumber(item.secondsAboveLimit),
                percent: formatNumber(item.percentAboveLimit),
                sessionDuration:
                  item.sessionDurationLabel || formatNumber(item.sessionSeconds),
              }
            )}
      </p>

      {item.whatsappEnabled ? (
        <p className="field-app-pending-action-hint">
          {t(
            "WhatsApp enviado ao gestor cadastrado (serviço pode gerar custo extra)."
          )}
        </p>
      ) : null}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function HarvestLossExceededActionCard({ item, farmId, t, onResolved }) {
  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--harvest-loss">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--harvest-loss">
            <FaLeaf aria-hidden />
            {t(item.actionLabel || "Perda de colheita acima do limite")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || t("Amostra de perda")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
            {item.serviceOrderId ? (
              <span>
                {t("Ordem de serviço")}: #{item.serviceOrderId}
              </span>
            ) : null}
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--harvest-loss">
          {t("Alerta")}
        </span>
      </header>

      {item.serviceOrderTitle ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Serviço")}:</strong> {item.serviceOrderTitle}
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        {t(
          "Amostra com {{kg}} kg/ha ({{bags}} sc/ha), acima do limite de alerta de {{limit}} kg/ha. Margem tolerada da ordem: {{tolerated}} kg/ha (+{{percent}}%).",
          {
            kg: formatNumber(item.kgPerHa),
            bags: formatNumber(item.bagsPerHa),
            limit: formatNumber(item.alertLimitKgHa),
            tolerated: formatNumber(item.toleratedKgHa),
            percent: formatNumber(item.alertThresholdPercent),
          }
        )}
      </p>

      {item.seedCount != null ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Sementes contadas")}:</strong>{" "}
          {formatNumber(item.seedCount)}
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        <strong>{t("Local da amostra")}:</strong> {formatLocation(item.location)}
      </p>

      <div className="field-app-pending-action-actions">
        {item.serviceOrderId ? (
          <Link
            to={`/${item.farmId || farmId}/pending-schedules/?obs=${item.serviceOrderId}&expand_loss=1`}
            className="btn btn-sm btn-default"
          >
            <FaClipboardList style={{ marginRight: 6 }} />
            {t("Abrir ordem de serviço")}
          </Link>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function MaintenanceOverdueActionCard({ item, t, onResolved }) {
  const [completeOpen, setCompleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const maintenanceLabel =
    item.maintenanceTitle || item.maintenanceName || "—";
  const nextDue =
    item.nextDueHours != null ? item.nextDueHours : item.targetHourMeter;
  const editHref = item.farmId
    ? `/${item.farmId}/field-app/?tab=maintenance`
    : null;
  const canComplete = Boolean(item.maintenanceId && item.farmId);
  const completeRow = {
    id: item.maintenanceId,
    equipment_name: item.machineName || item.machineIdent,
    name: maintenanceLabel,
    meterType: item.meterType,
    currentHourMeterDisplay: item.currentHourMeter,
  };

  const handleComplete = async (payload) => {
    setSaving(true);
    try {
      await completeScheduledMaintenance(
        item.farmId,
        item.maintenanceId,
        payload
      );
      toast.success(t("Manutenção concluída."));
      setCompleteOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (error) {
      const data = error && error.response && error.response.data;
      toast.error(
        (data && (data.hourMeter || data.odometer || data.completedDate)) ||
          t("Não foi possível concluir a manutenção.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--maintenance">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--maintenance">
            <FaWrench aria-hidden />
            {t(item.actionLabel || "Manutenção vencida")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Tarefa")}: {maintenanceLabel}
            </span>
            <span>
              {t("Próxima revisão")}: {formatNumber(nextDue)} h
            </span>
            <span>
              {t("Horímetro atual")}: {formatNumber(item.currentHourMeter)} h
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--maintenance">
          {t("Vencida")}
        </span>
      </header>

      {item.hoursOverdue != null ? (
        <p className="field-app-pending-action-note">
          {t("A máquina está {{hours}} h acima do horímetro programado para esta manutenção.", {
            hours: formatNumber(item.hoursOverdue),
          })}
        </p>
      ) : (
        <p className="field-app-pending-action-note">
          {t(
            "O horímetro atual da máquina atingiu ou ultrapassou o valor programado para a próxima revisão."
          )}
        </p>
      )}

      <div className="field-app-pending-action-actions">
        {editHref ? (
          <Link className="btn btn-sm btn-default" to={editHref}>
            {t("Ver agendamento")}
          </Link>
        ) : null}
        {canComplete ? (
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={() => setCompleteOpen(true)}
          >
            {t("Concluir manutenção hoje")}
          </button>
        ) : (
          <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
        )}
      </div>
      {completeOpen ? (
        <FieldAppCompleteMaintenanceModal
          row={completeRow}
          t={t}
          saving={saving}
          onClose={() => (saving ? null : setCompleteOpen(false))}
          onSubmit={handleComplete}
        />
      ) : null}
    </article>
  );
}

function PendingActionCard(props) {
  if (props.item.actionType === "maintenance_overdue") {
    return <MaintenanceOverdueActionCard {...props} />;
  }
  if (props.item.actionType === "meter_reading_anomaly") {
    return <MeterAnomalyActionCard {...props} />;
  }
  if (props.item.actionType === "fuel_supply_anomaly") {
    return <FuelSupplyAnomalyActionCard {...props} />;
  }
  if (props.item.actionType === "stale_meter_reading") {
    return <StaleMeterActionCard {...props} />;
  }
  if (props.item.actionType === "speed_violation") {
    return <SpeedViolationActionCard {...props} />;
  }
  if (props.item.actionType === "harvest_loss_exceeded") {
    return <HarvestLossExceededActionCard {...props} />;
  }
  return <RemoteFinishActionCard {...props} />;
}

function PendingActionCategorySection({
  category,
  items,
  farmId,
  t,
  onResolved,
  onOpenInTracking,
  onEditingChange,
  defaultExpanded,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!items.length) {
    return null;
  }

  return (
    <section className="field-app-pending-actions-group">
      <button
        type="button"
        className="field-app-pending-actions-group-head"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <span className="field-app-pending-actions-group-icon">
          {expanded ? <FaChevronDown /> : <FaChevronRight />}
        </span>
        <span className="field-app-pending-actions-group-title">{t(category.label)}</span>
        <span className="field-app-pending-actions-group-count">{items.length}</span>
      </button>
      {expanded ? (
        <div className="field-app-pending-actions-group-body">
          {items.map((item) => (
            <PendingActionCard
              key={`${item.actionType}-${item.sessionUuid || item.recordUuid || item.maintenanceId}`}
              item={item}
              farmId={farmId}
              t={t}
              onResolved={onResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={onEditingChange}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function FieldAppPendingActionsPanel({
  farmId,
  t,
  onOpenInTracking,
  alertCategory = "all",
}) {
  const location = useLocation();
  const {
    pendingItems,
    loading,
    refreshPendingActions,
    setPendingActionsEditing,
  } = usePendingActions();
  const [localLoading, setLocalLoading] = useState(false);

  const handleEditingChange = useCallback(
    (isEditing) => {
      setPendingActionsEditing(isEditing);
    },
    [setPendingActionsEditing]
  );

  const activeCategory = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get("alert");
    return fromUrl || alertCategory || "all";
  }, [alertCategory, location.search]);

  const reload = useCallback(async () => {
    setLocalLoading(true);
    try {
      await refreshPendingActions();
    } finally {
      setLocalLoading(false);
    }
  }, [refreshPendingActions]);

  useEffect(() => {
    reload();
  }, [farmId, reload]);

  const handleResolved = useCallback(async () => {
    await refreshPendingActions({ background: true });
  }, [refreshPendingActions]);

  const visibleItems = useMemo(
    () => filterPendingByCategory(pendingItems, activeCategory, farmId),
    [activeCategory, farmId, pendingItems]
  );

  const groupedSections = useMemo(() => {
    if (activeCategory !== "all") {
      return [];
    }
    return PENDING_ACTION_CATEGORIES.filter((category) => category.id !== "all")
      .map((category) => ({
        category,
        items: filterPendingByCategory(pendingItems, category.id, farmId),
      }))
      .filter((section) => section.items.length > 0);
  }, [activeCategory, farmId, pendingItems]);

  const activeCategoryMeta = resolvePendingCategory(activeCategory);
  const isLoading = loading || localLoading;

  return (
    <div className="field-app-pending-actions">
      <div className="field-app-pending-actions-head">
        <div>
          <h2>{t("Ações pendentes e alertas")}</h2>
          <p>
            {t(
              "Central de alertas da plataforma — encerramentos incompletos, leitura inconsistente de odômetro ou horímetro, odômetro ou horímetro sem variação, velocidade, perda de colheita, manutenção vencida e demais itens que precisam de atenção do gestor."
            )}
          </p>
          {activeCategory !== "all" ? (
            <p className="field-app-pending-actions-filter-note">
              {t("Filtrando")}: <strong>{t(activeCategoryMeta.label)}</strong>{" "}
              <Link to={`/${farmId}/field-app/?tab=pending-actions`}>
                {t("Ver todos")}
              </Link>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={reload}
          disabled={isLoading}
        >
          {t("Atualizar")}
        </button>
      </div>

      {isLoading ? (
        <div className="field-app-loading">
          <MDSpinner singleColor="#1ABB9C" size={40} />
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="field-app-pending-actions-empty">
          {activeCategory === "speed_violation" ? (
            <>
              <p>{t("Nenhum alerta de velocidade no momento.")}</p>
              <p className="text-muted">
                {t(
                  "O alerta é gerado ao encerrar um trabalho no app, comparando a velocidade GPS do turno com o limite cadastrado em Equipamentos → Máquinas (Velocidade máxima). Se a máquina ficou acima do limite por tempo relevante, o alerta aparece aqui."
                )}
              </p>
            </>
          ) : (
            <p>{t("Nenhuma ação pendente ou alerta no momento.")}</p>
          )}
        </div>
      ) : activeCategory === "all" ? (
        <div className="field-app-pending-actions-groups">
          {groupedSections.map((section) => (
            <PendingActionCategorySection
              key={section.category.id}
              category={section.category}
              items={section.items}
              farmId={farmId}
              t={t}
              onResolved={handleResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={handleEditingChange}
              defaultExpanded
            />
          ))}
        </div>
      ) : (
        <div className="field-app-pending-actions-list">
          {visibleItems.map((item) => (
            <PendingActionCard
              key={`${item.actionType}-${item.sessionUuid || item.recordUuid || item.maintenanceId}`}
              item={item}
              farmId={farmId}
              t={t}
              onResolved={handleResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={handleEditingChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
